import React, { useState } from 'react';
import styled from 'styled-components';
import api from '../api';
import { theme } from '../theme';
import { formatSubathonClock, useSubathonAdmin } from '../hooks/useSubathonAdmin';
import SubathonControlTab from '../components/subathon/SubathonControlTab';
import SubathonRulesTab from '../components/subathon/SubathonRulesTab';
import SubathonActivityTab from '../components/subathon/SubathonActivityTab';
import SubathonOverlaysTab from '../components/subathon/SubathonOverlaysTab';
import SubathonHistoryTab from '../components/subathon/SubathonHistoryTab';
import {
  Button,
  ErrorText,
  Muted,
  Page,
  PageHeader,
  PageHeaderMain,
  Subtitle,
  TabBar,
  TabBtn,
  Title,
} from '../components/ui';

type Tab = 'control' | 'rules' | 'activity' | 'overlays' | 'history';

const TABS: { id: Tab; label: string }[] = [
  { id: 'control', label: 'Control' },
  { id: 'rules', label: 'Reglas' },
  { id: 'activity', label: 'Actividad' },
  { id: 'overlays', label: 'Overlays' },
  { id: 'history', label: 'Historial' },
];

const LiveStrip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 20, 147, 0.28);
  background: rgba(255, 20, 147, 0.08);
  font-size: 0.86rem;
  color: #fff;
  font-variant-numeric: tabular-nums;

  small {
    color: ${theme.colors.textMuted};
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }
`;

const HeaderSide = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const StatePill = styled.span<{ $off?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  border: 1px solid ${({ $off }) => ($off ? 'rgba(255, 255, 255, 0.18)' : 'rgba(157, 255, 176, 0.35)')};
  background: ${({ $off }) => ($off ? 'rgba(255, 255, 255, 0.05)' : 'rgba(157, 255, 176, 0.1)')};
  color: ${({ $off }) => ($off ? theme.colors.textMuted : '#9dffb0')};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $off }) => ($off ? theme.colors.textMuted : '#9dffb0')};
  }
`;

const SubathonPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('control');
  const [toggling, setToggling] = useState(false);
  const { state, remainingMs, loading, error, refresh, run } = useSubathonAdmin();

  const enabled = state?.enabled !== false;

  // Apagado, el extensible no suma tiempo ni acepta cargas. No avisa nada en el chat.
  const toggleSubathon = async () => {
    setToggling(true);
    const ok = await run(api.put('/subathon/settings', { enabled: !enabled }));
    setToggling(false);
    if (ok) await refresh();
  };

  const statusLabel = () => {
    if (!state) return '';
    if (state.timer.status === 'idle') return 'Sin extensible';
    if (state.timer.status === 'finished') return 'Finalizado';
    if (state.timer.waiting) return 'Esperando aporte';
    if (state.timer.status === 'paused') return 'En pausa';
    return 'En vivo';
  };

  return (
    <Page>
      <PageHeader>
        <PageHeaderMain>
          <Title>Extensible</Title>
          <Subtitle>
            El stream se extiende solo: cada follow, sub, bits, cafecito o cofre suma minutos al
            contador según las reglas que definas.
          </Subtitle>
        </PageHeaderMain>
        <HeaderSide>
          <StatePill $off={!enabled}>
            {enabled ? 'Extensible encendido' : 'Extensible apagado'}
          </StatePill>
          <Button
            $variant={enabled ? 'ghost' : undefined}
            $size="sm"
            disabled={toggling || !state}
            onClick={toggleSubathon}
          >
            {toggling ? '…' : enabled ? 'Apagar extensible' : 'Encender extensible'}
          </Button>
          {state && (
            <LiveStrip>
              <small>{statusLabel()}</small>
              <strong>{formatSubathonClock(remainingMs)}</strong>
              {state.totals.events > 0 && <small>{state.totals.events} aportes</small>}
            </LiveStrip>
          )}
        </HeaderSide>
      </PageHeader>

      {!enabled && state && (
        <Muted style={{ marginBottom: '0.6rem' }}>
          El extensible está apagado: no suma tiempo por follows, subs, bits ni donaciones, y no se
          puede iniciar. El panel y los overlays siguen funcionando.
        </Muted>
      )}

      <TabBar>
        {TABS.map((item) => (
          <TabBtn key={item.id} $active={tab === item.id} onClick={() => setTab(item.id)}>
            {item.label}
          </TabBtn>
        ))}
      </TabBar>

      {error && <ErrorText>{error}</ErrorText>}
      {loading && !state && <Muted>Cargando el extensible…</Muted>}

      {state && tab === 'control' && (
        <SubathonControlTab state={state} remainingMs={remainingMs} run={run} refresh={refresh} />
      )}
      {tab === 'rules' && <SubathonRulesTab onSaved={refresh} />}
      {state && tab === 'activity' && (
        <SubathonActivityTab version={state.totals.events} refresh={refresh} />
      )}
      {tab === 'overlays' && <SubathonOverlaysTab />}
      {tab === 'history' && <SubathonHistoryTab />}
    </Page>
  );
};

export default SubathonPage;
